
import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Mail, Check, AlertCircle, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { removeFromMarketingList } from "@/services/brevoService";
import { useAnalytics } from "@/hooks/useAnalytics";
import { Helmet } from "react-helmet";

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    document.title = "Peruuta markkinointilupa | Lainasampo.fi";
    trackEvent('page_view', { page: 'unsubscribe' });
  }, [trackEvent]);

  const handleUnsubscribe = async () => {
    if (!email) {
      setError("Sähköpostiosoite puuttuu URL-parametrista. Tarkista, että linkki on oikea ja sisältää sähköpostiosoitteesi.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Call the removeFromMarketingList function to handle unsubscribe
      const result = await removeFromMarketingList(email);

      if (!result.success) {
        throw new Error(result.error || "Tuntematon virhe tapahtui");
      }

      setIsSuccess(true);
      trackEvent('unsubscribe', { success: true });
      toast({
        title: "Markkinointilupa peruutettu",
        description: "Sinut on poistettu markkinointilistaltamme.",
      });
    } catch (err) {
      console.error("Error unsubscribing:", err);
      setError(err instanceof Error ? err.message : "Tuntematon virhe tapahtui.");
      trackEvent('unsubscribe', { success: false, error: err instanceof Error ? err.message : "Unknown error" });
      toast({
        title: "Virhe tapahtui",
        description: "Markkinointiluvan peruuttaminen epäonnistui. Ole hyvä ja yritä uudelleen tai ota yhteyttä asiakaspalveluumme.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-trigger unsubscribe if email is present
  useEffect(() => {
    if (email && !isSuccess && !isSubmitting && !error) {
      handleUnsubscribe();
    }
  }, [email]);

  return (
    <>
      <Helmet>
        <title>Peruuta markkinointilupa | Lainasampo.fi</title>
        <meta name="description" content="Peruuta markkinointilupa ja lopeta Lainasampo.fi markkinointiviestien vastaanottaminen." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-blue-50 py-12 px-4">
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/" className="inline-flex items-center text-blue-600 mb-6 hover:underline">
              <ArrowLeft size={16} className="mr-2" />
              Takaisin etusivulle
            </Link>

            <Card className="shadow-lg border-0">
              <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <CardTitle className="text-xl flex items-center">
                  <Mail className="mr-2" size={20} />
                  Peruuta markkinointilupa
                </CardTitle>
                <CardDescription className="text-blue-100">
                  Markkinointiviestien tilauksen peruuttaminen
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6">
                {isSuccess ? (
                  <div className="text-center py-4">
                    <div className="bg-green-100 text-green-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Check size={32} />
                    </div>
                    <h3 className="text-xl font-medium mb-2">Markkinointilupa peruutettu</h3>
                    <p className="text-gray-600 mb-4">
                      Sähköpostiosoitteesi <span className="font-medium">{email}</span> on poistettu markkinointilistaltamme.
                      Et enää vastaanota markkinointiviestejä meiltä.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="mb-6">
                      <p className="text-gray-700 mb-4">
                        {isSubmitting ? "Käsitellään peruutusta..." : "Olet peruuttamassa markkinointiluvan sähköpostiosoitteelle:"}
                      </p>
                      {email ? (
                        <div className="bg-gray-100 p-3 rounded-md text-gray-800 font-medium break-all">
                          {email}
                        </div>
                      ) : (
                        <div className="bg-red-50 p-3 rounded-md text-red-700 border border-red-200">
                          Sähköpostiosoite puuttuu URL-parametrista. Tarkista, että linkki on oikea.
                        </div>
                      )}
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-4 flex items-start">
                        <AlertCircle className="mr-2 flex-shrink-0 mt-1" size={16} />
                        <p>{error}</p>
                      </div>
                    )}

                    <p className="text-gray-600 text-sm mb-4">
                      Kun peruutat markkinointiluvan, et enää vastaanota markkinointiviestejä
                      tai tarjousviestejä Lainasampo.fi palvelulta.
                    </p>
                  </>
                )}
              </CardContent>

              {!isSuccess && !isSubmitting && (
                <CardFooter className="flex justify-end gap-2 bg-gray-50 border-t">
                  <Link to="/">
                    <Button variant="outline">Peruuta</Button>
                  </Link>
                  <Button 
                    disabled={isSubmitting || !email} 
                    onClick={handleUnsubscribe}
                  >
                    {isSubmitting ? "Käsitellään..." : "Peruuta markkinointilupa"}
                  </Button>
                </CardFooter>
              )}
            </Card>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default UnsubscribePage;
